/**
 * AP Invoices Demo — per-line "Document" column cell.
 *
 * THROWAWAY DEMO CODE. Renders the proof-of-delivery matched to an invoice line
 * as a chip; clicking opens the *extracted* document (structured fields + line
 * items) in-app rather than the raw scan — matching the demo's "review the
 * extracted data, not the PDF" theme. Inert (renders nothing) unless the demo
 * has extracted-document data for the matched CORE_DOCUMENT.
 */

import { useState } from 'react';
import { FileTextIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { T, Num } from 'gt-react';
import { cn } from '@/lib/utils';
import { ExtractedDocumentDialog } from '@/components/accounts-payable/invoice-detail/extracted-document-dialog';
import { useApExtractedDocument } from './provenance';

const KIND_LABEL = {
  invoice: 'Invoice',
  purchase_order: 'Purchase Order',
  proof_of_delivery: 'Proof of Delivery',
} as const;

export function ApDocumentColumnCell({ documentId }: { documentId: string }) {
  const doc = useApExtractedDocument(documentId);
  const [open, setOpen] = useState(false);

  if (!doc) return null;

  const label = KIND_LABEL[doc.kind];
  const lineCount = doc.lineItems.length;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          'flex items-center gap-2 rounded-md border px-2 py-1 max-w-[220px] text-left',
          'border-green-500 bg-green-50 hover:bg-green-100 transition-colors',
        )}
      >
        <FileTextIcon className="size-4 shrink-0 text-green-600" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1 text-xs font-medium">
            <span className="truncate">{label}</span>
            <ArrowSquareOutIcon className="size-3 shrink-0 text-muted-foreground" />
          </span>
          <span className="block text-[10px] text-muted-foreground">
            <T context="number of extracted line items on a matched document">
              extracted · <Num>{lineCount}</Num> lines
            </T>
          </span>
        </span>
      </button>
      <ExtractedDocumentDialog open={open} onOpenChange={setOpen} doc={doc} />
    </>
  );
}
